import { encodeSqrtRatioX96, TickMath, nearestUsableTick } from '@uniswap/v3-sdk';
import { toBigint, toJSBI } from '@/utils/bigInt';
import { PRICE_SCALE, Q192 } from '@/utils/constants';

/**
 *
 * @param tick
 * @returns price scaled by 1e18
 *
 *  sqrtRatioX96 = sqrt(P) Q96
 * => sqrtRatioX96^2 = P Q192
 * => P = sqrtRatioX96^2 / Q192
 * => P * 1e18 = sqrtRatioX96^2 * 1e18 / Q192
 */
export const tickToPriceScaled = (tick: number): bigint => {
  const sqrtRatioX96 = toBigint(TickMath.getSqrtRatioAtTick(tick));
  const ratioX192 = sqrtRatioX96 * sqrtRatioX96;
  return (ratioX192 * PRICE_SCALE) / Q192;
};

export const encodeSqrtRatioX96SingleValue = (priceScaled: bigint): bigint => {
  return toBigint(encodeSqrtRatioX96(toJSBI(priceScaled), toJSBI(PRICE_SCALE)));
};

// https://github.com/Uniswap/v3-sdk/blob/e10fb50efcbdefe7ed9b18962ce4cad51cf30d72/src/utils/priceTickConversions.ts#L29
export const priceToClosestTick = (price: number, tickSpacing: number | undefined): number => {
  const priceScaled = BigInt(price) * PRICE_SCALE;
  const sqrtRatioX96: bigint = encodeSqrtRatioX96SingleValue(priceScaled);
  let tick = TickMath.getTickAtSqrtRatio(toJSBI(sqrtRatioX96));
  const nextTickPriceScaled: bigint = tickToPriceScaled(tick + 1);
  if (priceScaled >= nextTickPriceScaled) {
    tick++;
  }
  if (tickSpacing !== undefined) {
    return nearestUsableTick(tick, tickSpacing);
  }
  return tick;
};

/*
 *
 * Uniswap v3/v4 pools have a tick range from a minimum of -887272 to a maximum of 887272.
 * Ticks used for positions in upper and lower ranges must be evenly divisible by the tick spacing.
 *
 */
export const getMaxMinTick = (
  tickSpacing = 1,
): {
  maxTick: number;
  minTick: number;
} => {
  return {
    maxTick: nearestUsableTick(TickMath.MAX_TICK, tickSpacing),
    minTick: nearestUsableTick(TickMath.MIN_TICK, tickSpacing),
  };
};
